import { Link } from "react-router-dom";
import { Clock, Flame } from "lucide-react";
import { useH3Promo } from "@/hooks/use-h3-promo";
import { POWER_TIER_LABEL } from "@/config/models";
import { H3_PROMO_DISCOUNT_LABEL } from "@/config/h3-promo";

/**
 * Top-of-page flash-sale strip. Shows a cooler teal "coming soon"
 * countdown before the sale starts, a hot orange countdown while it's
 * live, and returns `null` the moment the hardcoded deadline passes —
 * the regular "Extend Video" banner takes its place automatically.
 *
 * Mobile and desktop render as two completely separate rows so mobile
 * can lead with COMING SOON (instead of hiding it behind a breakpoint)
 * — users should never see a bare "60% OFF" + timer during the teaser
 * phase and mistake it for a live sale.
 */
export function H3PromoBanner() {
  const { phase, countdownLabel } = useH3Promo();
  if (phase === "ended") return null;

  const isActive = phase === "active";
  const Icon = isActive ? Flame : Clock;
  const bgClass = isActive
    ? "bg-gradient-to-r from-[#b8390f] via-[#e8822e] to-[#b8390f]"
    : "bg-gradient-to-r from-[#1e4a52] via-[#2f6b74] to-[#1e4a52]";
  const accentClass = isActive ? "text-amber-100" : "text-[#d5f0ea]";

  return (
    <Link
      to="/create/image-to-video"
      className={`shrink-0 z-40 flex items-center justify-center ${bgClass} text-white text-center text-[11px] sm:text-[13px] font-extrabold py-2 px-3 sm:px-4 hover:brightness-110 transition-all uppercase tracking-wide whitespace-nowrap`}
    >
      {/* Mobile layout (<md) */}
      <span className="md:hidden flex items-center justify-center gap-x-2">
        <Icon className={`size-3.5 shrink-0 ${accentClass}`} strokeWidth={2.5} />
        {isActive ? (
          <>
            <span>
              {H3_PROMO_DISCOUNT_LABEL} {POWER_TIER_LABEL}
            </span>
            <span className="text-white/40">·</span>
            <span className={`tabular-nums ${accentClass}`}>{countdownLabel}</span>
          </>
        ) : (
          <>
            <span>Coming Soon</span>
            <span className="text-white/40">·</span>
            <span>{H3_PROMO_DISCOUNT_LABEL}</span>
            <span className="text-white/40">·</span>
            <span className={`tabular-nums ${accentClass}`}>{countdownLabel}</span>
          </>
        )}
      </span>

      {/* Desktop layout (md+) */}
      <span className="hidden md:flex items-center justify-center gap-x-3">
        <Icon className={`size-4 shrink-0 ${accentClass}`} strokeWidth={2.5} />
        <span>
          {H3_PROMO_DISCOUNT_LABEL} {POWER_TIER_LABEL} ·{" "}
          {isActive ? "24-hour flash sale" : "coming soon"}
        </span>
        <span className="text-white/40">·</span>
        <span className={`tabular-nums ${accentClass}`}>
          {isActive ? "ends in" : "starts in"} {countdownLabel}
        </span>
      </span>
    </Link>
  );
}
